import { useMemo } from 'react'
import { useGameStore } from '@store/gameStore'
import './EndScreen.css'

// Determine speedrun category based on money spent
const getCategory = (moneySpent: number) => {
    if (moneySpent === 0) return { name: 'NO MONEY', emoji: '🏆', color: 'gold' }
    if (moneySpent < 1) return { name: 'AD-ONLY', emoji: '📺', color: 'info' }
    if (moneySpent < 10) return { name: 'LOW SPEND', emoji: '💵', color: 'success' }
    if (moneySpent < 100) return { name: 'CASUAL', emoji: '💳', color: 'warning' }
    return { name: 'WHALE%', emoji: '🐋', color: 'danger' }
}

// Generate satirical comment based on stats
const generateComment = (
    moneySpent: number,
    adsWatched: number,
    totalTimeMinutes: number
) => {
    if (moneySpent > 1000) {
        return "Du hast mehr ausgegeben als für einen Urlaub. Gratulation?"
    }
    if (moneySpent > 100) {
        return "Du hast gewonnen. Das hat dich ordentlich was gekostet."
    }
    if (moneySpent > 10) {
        return "Fast geschafft ohne viel zu zahlen. Fast."
    }
    if (adsWatched > 50) {
        return "Du hast mehr Werbung geschaut als Netflix. Respekt."
    }
    if (adsWatched > 20) {
        return "Deine Augen gehören jetzt den Werbetreibenden."
    }
    if (moneySpent === 0 && adsWatched === 0) {
        return "Unglaublich! Du hast das System besiegt. Oder?"
    }
    if (totalTimeMinutes > 180) {
        return "Du hast mit deiner Lebenszeit bezahlt. Die wertvollste Währung."
    }
    return "Du hast gewonnen. War es das wert?"
}

export function EndScreen() {
    const gameStartTime = useGameStore(state => state.gameStartTime)
    const realMoneySpent = useGameStore(state => state.realMoneySpent)
    const adsWatched = useGameStore(state => state.adsWatched)
    const totalWaitTimeMs = useGameStore(state => state.totalWaitTimeMs)
    const resetGame = useGameStore(state => state.resetGame)
    const setScreen = useGameStore(state => state.setScreen)

    // Calculate stats
    const stats = useMemo(() => {
        const now = Date.now()
        const totalPlayTimeMs = gameStartTime ? now - gameStartTime : 0
        const totalPlayTimeMinutes = Math.floor(totalPlayTimeMs / 60000)
        const waitTimeMinutes = Math.floor(totalWaitTimeMs / 60000)
        const activeTimeMinutes = totalPlayTimeMinutes - waitTimeMinutes

        // Efficiency: How much of your time was "active" vs waiting
        const efficiency = totalPlayTimeMinutes > 0
            ? Math.round((activeTimeMinutes / totalPlayTimeMinutes) * 100)
            : 0

        // Cost per minute of gameplay
        const costPerMinute = activeTimeMinutes > 0
            ? realMoneySpent / activeTimeMinutes
            : 0

        return {
            totalPlayTime: {
                hours: Math.floor(totalPlayTimeMinutes / 60),
                minutes: totalPlayTimeMinutes % 60,
            },
            waitTime: {
                hours: Math.floor(waitTimeMinutes / 60),
                minutes: waitTimeMinutes % 60,
            },
            efficiency,
            costPerMinute,
            totalPlayTimeMinutes,
        }
    }, [gameStartTime, totalWaitTimeMs, realMoneySpent])

    const category = getCategory(realMoneySpent)
    const comment = generateComment(
        realMoneySpent,
        adsWatched,
        stats.totalPlayTimeMinutes
    )

    // Format time display
    const formatTime = (hours: number, minutes: number) => {
        if (hours > 0) {
            return `${hours}h ${minutes}min`
        }
        return `${minutes}min`
    }

    // Handle restart
    const handleRestart = () => {
        resetGame()
        setScreen('landing')
    }

    return (
        <div className="endscreen">
            {/* Victory Banner */}
            <div className="endscreen__banner">
                <span className="endscreen__trophy">🏆</span>
                <h1 className="endscreen__title">DU HAST GEWONNEN!</h1>
                <p className="endscreen__subtitle">RANG #1 ERREICHT</p>
            </div>

            {/* Stats Card */}
            <div className="endscreen__stats">
                <h2 className="endscreen__stats-title">📊 DEINE ABRECHNUNG:</h2>

                <div className="endscreen__stat-row">
                    <span className="endscreen__stat-icon">⏱️</span>
                    <span className="endscreen__stat-label">Spielzeit:</span>
                    <span className="endscreen__stat-value">
                        {formatTime(stats.totalPlayTime.hours, stats.totalPlayTime.minutes)}
                    </span>
                </div>

                <div className="endscreen__stat-row endscreen__stat-row--highlight">
                    <span className="endscreen__stat-icon">💸</span>
                    <span className="endscreen__stat-label">Geld ausgegeben:</span>
                    <span className="endscreen__stat-value endscreen__stat-value--money">
                        {realMoneySpent.toFixed(2)}€
                    </span>
                </div>

                <div className="endscreen__stat-row">
                    <span className="endscreen__stat-icon">📺</span>
                    <span className="endscreen__stat-label">Werbung geschaut:</span>
                    <span className="endscreen__stat-value">{adsWatched}×</span>
                </div>

                <div className="endscreen__stat-row">
                    <span className="endscreen__stat-icon">⏳</span>
                    <span className="endscreen__stat-label">Gewartet:</span>
                    <span className="endscreen__stat-value">
                        {formatTime(stats.waitTime.hours, stats.waitTime.minutes)}
                    </span>
                </div>

                <div className="endscreen__stat-row">
                    <span className="endscreen__stat-icon">📈</span>
                    <span className="endscreen__stat-label">Effizienz:</span>
                    <span className="endscreen__stat-value">{stats.efficiency}%</span>
                </div>

                {realMoneySpent > 0 && (
                    <div className="endscreen__stat-row endscreen__stat-row--subtle">
                        <span className="endscreen__stat-icon">💰</span>
                        <span className="endscreen__stat-label">Kosten pro Minute:</span>
                        <span className="endscreen__stat-value">
                            {stats.costPerMinute.toFixed(2)}€
                        </span>
                    </div>
                )}
            </div>

            {/* Category Badge */}
            <div className={`endscreen__category endscreen__category--${category.color}`}>
                <span className="endscreen__category-emoji">{category.emoji}</span>
                <span className="endscreen__category-name">Kategorie: {category.name}</span>
            </div>

            {/* Comment */}
            <div className="endscreen__comment">
                <p className="endscreen__quote">"{comment}"</p>
            </div>

            {/* Final Message */}
            <div className="endscreen__final-message">
                <p>
                    "Du hast gewonnen.
                    <br />
                    Das hat dich <strong>
                        {formatTime(stats.totalPlayTime.hours, stats.totalPlayTime.minutes)}
                    </strong> und <strong>{realMoneySpent.toFixed(2)}€</strong> gekostet."
                </p>
            </div>

            {/* Actions */}
            <div className="endscreen__actions">
                <button
                    className="btn btn--gold btn--lg btn--full"
                    onClick={handleRestart}
                >
                    🔄 Nochmal spielen
                </button>
                <button
                    className="btn btn--ghost btn--full"
                    onClick={() => setScreen('leaderboard')}
                >
                    🏆 Bestenliste ansehen
                </button>
            </div>

            {/* Share Section */}
            <div className="endscreen__share">
                <p>📤 Ergebnis teilen:</p>
                <div className="endscreen__share-buttons">
                    <button className="endscreen__share-btn">🐦</button>
                    <button className="endscreen__share-btn">📱</button>
                    <button className="endscreen__share-btn">📋</button>
                </div>
            </div>
        </div>
    )
}
